import type { CSSProperties, ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { MinusIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

type StickyPanelProps = {
  title: string;
  content: ReactNode;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  defaultPosition?: { x: number; y: number };
  defaultSize?: { width: number; height: number; minimizedHeight?: number };
  dockRight?: boolean;
  showCloseButton?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const StickyPanel = ({
  title,
  content,
  defaultOpen = true,
  forceOpen = false,
  defaultPosition = { x: 24, y: 120 },
  defaultSize = { width: 360, height: 600, minimizedHeight: 44 },
  dockRight = false,
  showCloseButton = false,
}: StickyPanelProps) => {
  const getDockedPosition = (minimized: boolean) => ({
    x: Math.max(16, window.innerWidth - defaultSize.width - 16),
    y: minimized
      ? Math.max(16, window.innerHeight - (defaultSize.minimizedHeight ?? 44) - 16)
      : 16,
  });

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Start docked on the right when dockRight is set, so the panel doesn't paint
  // at the top-left default and then jump right (visible flicker on open).
  const [position, setPosition] = useState(() => (dockRight ? getDockedPosition(false) : defaultPosition));
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });

  useEffect(() => {
    if (!dockRight) return;
    const nextPosition = getDockedPosition(isMinimized);
    setPosition(nextPosition);
  }, [dockRight, defaultSize.width, defaultSize.minimizedHeight, isMinimized]);

  useEffect(() => {
    if (!forceOpen) return;
    setIsOpen(true);
    setIsMinimized(false);
  }, [forceOpen]);

  useEffect(() => {
    if (!isDragging) return;
    const handlePointerMove = (event: PointerEvent) => {
      if (!dragState.current.isDragging) return;
      const container = panelRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const maxX = Math.max(8, window.innerWidth - rect.width - 8);
      const maxY = Math.max(8, window.innerHeight - rect.height - 8);
      const nextX = clamp(event.clientX - dragState.current.offsetX, 8, maxX);
      const nextY = clamp(event.clientY - dragState.current.offsetY, 8, maxY);
      setPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = () => {
      if (!dragState.current.isDragging) return;
      dragState.current.isDragging = false;
      setIsDragging(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [isDragging]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isMaximized || !isOpen) return;
    if (event.button !== 0) return;
    const container = panelRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragState.current.isDragging = true;
    dragState.current.offsetX = event.clientX - rect.left;
    dragState.current.offsetY = event.clientY - rect.top;
    setIsDragging(true);
    if (event.currentTarget?.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handleMinimize = () => {
    setIsMinimized((prev) => {
      const next = !prev;
      if (next) {
        setIsMaximized(false);
        if (dockRight) {
          setPosition(getDockedPosition(true));
        }
      }
      return next;
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    setIsMaximized(false);
    setIsDragging(false);
  };

  const panelStyle: CSSProperties = isMaximized
    ? { left: 16, top: 16, width: "calc(100vw - 32px)", height: "calc(100vh - 32px)" }
    : {
      left: position.x,
      top: position.y,
      width: defaultSize.width,
      height: isMinimized
        ? defaultSize.minimizedHeight ?? 44
        : dockRight
          ? "calc(100vh - 32px)"
          : defaultSize.height,
    };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={clsx(
        "fixed z-50 flex flex-col overflow-hidden rounded-[12px] border-[1.5px] border-[#fff] bg-white shadow-[inset_0_4px_8px_rgba(255,255,255,0.43),_-4px_4px_12px_rgba(0,0,0,0.05)]",
        isMaximized ? "cursor-default" : "cursor-grab",
      )}
      style={panelStyle}
    >
      <div
        className={clsx(
          "flex items-center justify-between border-b border-gray-100 bg-gray-50  select-none",
          isMaximized ? "cursor-default" : isDragging ? "cursor-grabbing" : "cursor-grab",
        )}
        onPointerDown={handlePointerDown}
        role="button"
        aria-label="Drag sticky panel"
      >

        <div className="bg-[linear-gradient(90deg,#FFC9DC_0%,#FFEEF8_100%)] flex items-center gap-4 p-[8px] justify-between w-full">
          <h3 className="text-[14px] font-semibold ">{title}</h3>
          <div className="flex justify-around gap-2">
            <button
              type="button"
              onClick={handleMinimize}
              onPointerDown={(event) => event.stopPropagation()}
              className="inline-flex h-7 w-7 items-center justify-center "
              aria-label={isMinimized ? "Restore panel" : "Minimize panel"}
            >
              {/* <MinusIcon className="h-[16px] w-[16px]" /> */}
              {isMinimized ? (
                <PlusIcon className="h-[16px] w-[16px]" />
              ) : (
                <MinusIcon className="h-[16px] w-[16px]" />
              )}
            </button>
            {showCloseButton && (
              <button
                type="button"
                onClick={handleClose}
                onPointerDown={(event) => event.stopPropagation()}
                className="inline-flex h-7 w-7 items-center justify-center"
                aria-label="Close panel"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      {!isMinimized && <div className="flex-1 overflow-auto">{content}</div>}
    </div>
  );
};

export default StickyPanel;
